import { Ioc } from "./ioc";
import { FrameModel, FRAME_MODEL } from "@core/frame/frame";
import { EngineService, ENGINE_SERVICE } from "@runtime/engine/engineService";
import {
  AmbientOcclusionModel,
  AMBIENT_OCCLUSION_MODEL,
} from "@runtime/engine/ambientOcclusion";
import { RenderQualityModel, RENDER_QUALITY_MODEL } from "@runtime/engine/renderQuality";
import { InputService, INPUT_SERVICE } from "@runtime/input/inputService";
import { PickingService, PICKING_SERVICE } from "@runtime/picking/pickingService";
import {
  EntityAnimationPlayback,
  ENTITY_ANIMATION_PLAYBACK,
} from "@runtime/assets/entityAnimationPlayback";
import {
  SceneStatisticsService,
  SCENE_STATISTICS_SERVICE,
} from "@runtime/diagnostics/SceneStatisticsService";
import { XRSessionService, XR_SESSION_SERVICE } from "@runtime/xr/XRSessionService";
import {
  CONSOLE_PLACEMENT_MODEL,
  type IConsolePlacementModel,
} from "@models/xr/IConsolePlacementModel";
import { ConsolePlacementModel } from "@models/xr/ConsolePlacementModel";
import {
  XRPanelSourceRegistry,
  XR_PANEL_SOURCE_REGISTRY,
} from "@runtime/xr/XRPanelSourceRegistry";
import { EDITOR_UI_MODEL, type IEditorUIModel } from "@models/ui/IEditorUIModel";
import { EditorUIModel } from "@models/ui/EditorUIModel";
import { RIGHT_PANEL_UI_MODEL, type IRightPanelUIModel } from "@models/ui/IRightPanelUIModel";
import { RightPanelUIModel } from "@models/ui/RightPanelUIModel";
import { DEBUG_MODE_MODEL, type IDebugModeModel } from "@models/ui/IDebugModeModel";
import { DebugModeModel } from "@models/ui/DebugModeModel";
import {
  DEPLOYMENTS_MODEL,
  type IDeploymentsModel,
} from "@models/deployments/IDeploymentsModel";
import { DeploymentsModel } from "@models/deployments/DeploymentsModel";
import { DEV_SETTINGS_MODEL, type IDevSettingsModel } from "@models/ui/IDevSettingsModel";
import { DevSettingsModel } from "@models/ui/DevSettingsModel";
import { APP_SETTINGS_MODEL, type IAppSettingsModel } from "@models/settings/IAppSettingsModel";
import {
  DEPLOYMENT_CONFIG_MODEL,
  type IDeploymentConfigModel,
} from "@models/config/IDeploymentConfigModel";
import { DeploymentConfigModel } from "@models/config/DeploymentConfigModel";
import { AppSettingsModel } from "@models/settings/AppSettingsModel";
import {
  AI_PROMPT_STORE_MODEL,
  type IAIPromptStoreModel,
} from "@ai/guardrails/IAIPromptStoreModel";
import { AIPromptStoreModel } from "@ai/guardrails/AIPromptStoreModel";
import {
  AuthWakeRefreshService,
  AUTH_WAKE_REFRESH_SERVICE,
  type IAuthWakeRefreshService,
} from "@runtime/lifecycle/AuthWakeRefreshService";
import { ProjectModel, PROJECT_MODEL } from "@models/project/ProjectModel";
import type { IProjectListModel } from "@models/project/IProjectListModel";
import { PROJECT_LIST_MODEL } from "@models/project/IProjectListModel";
import { HttpProjectListModel } from "@models/project/HttpProjectListModel";
import { AssetLibraryModel } from "@models/assets/AssetLibraryModel";
import {
  ASSET_LIBRARY_MODEL,
  type IAssetLibraryModel,
} from "@models/assets/IAssetLibraryModel";
import { UndoRedo } from "@models/project/UndoRedo";
import { UNDO_REDO } from "@models/project/IUndoRedo";
import { DuplicationModel } from "@models/project/DuplicationModel";
import type { IDuplicationModel } from "@models/project/IDuplicationModel";
import { DUPLICATION_MODEL } from "@models/project/IDuplicationModel";
import { MergeModel } from "@models/project/MergeModel";
import type { IMergeModel } from "@models/project/IMergeModel";
import { MERGE_MODEL } from "@models/project/IMergeModel";
import { SceneModel } from "@models/scene/SceneModel";
import { SCENE_MODEL } from "@models/scene/ISceneModel";
import { SkyboxModel, SKYBOX_MODEL } from "@models/scene/SkyboxModel";
import {
  SkyboxImportModel,
  SKYBOX_IMPORT_MODEL,
} from "@models/scene/SkyboxImportModel";
import { SelectionModel } from "@models/scene/SelectionModel";
import { SELECTION_MODEL } from "@models/scene/ISelectionModel";
import { HoverModel } from "@models/scene/HoverModel";
import { HOVER_MODEL } from "@models/scene/IHoverModel";
import { InMemoryToolbarModel } from "@models/tools/InMemoryToolbarModel";
import { TOOLBAR_MODEL } from "@models/tools/IToolbarModel";
import { presenterToolFor } from "@models/tools/toolPermissions";
import { PlacementModel } from "@models/tools/PlacementModel";
import { PLACEMENT_MODEL } from "@models/tools/IPlacementModel";
import { SurfacePlacementModel } from "@models/tools/SurfacePlacementModel";
import {
  SURFACE_PLACEMENT_MODEL,
  type ISurfacePlacementModel,
} from "@models/tools/ISurfacePlacementModel";
import { CommonSliceToolModel } from "@models/tools/CommonSliceToolModel";
import {
  COMMON_SLICE_TOOL_MODEL,
  type ICommonSliceToolModel,
} from "@models/tools/ICommonSliceToolModel";
import { BoundingBoxToolModel } from "@models/tools/BoundingBoxToolModel";
import { MeasurementsModel } from "@models/tools/MeasurementsModel";
import {
  MEASUREMENTS_MODEL,
  type IMeasurementsModel,
} from "@models/tools/IMeasurementsModel";
import {
  BOUNDING_BOX_TOOL_MODEL,
  type IBoundingBoxToolModel,
} from "@models/tools/IBoundingBoxToolModel";
import {
  LaserToolModel,
  LASER_TOOL_MODEL,
} from "@models/tools/LaserToolModel";
import { ExplosionModifierModel } from "@models/modifiers/ExplosionModifierModel";
import type { IExplosionModifierModel } from "@models/modifiers/IExplosionModifierModel";
import { EXPLOSION_MODIFIER_MODEL } from "@models/modifiers/IExplosionModifierModel";
import { GrabModifierModel } from "@models/modifiers/GrabModifierModel";
import {
  GRAB_MODIFIER_MODEL,
  type IGrabModifierModel,
} from "@models/modifiers/IGrabModifierModel";
import {
  NetworkGrabUpdateModel,
  NETWORK_GRAB_UPDATE_MODEL,
} from "@models/tools/NetworkGrabUpdateModel";
import type { IMaterialModel } from "@models/materials/IMaterialModel";
import { MATERIAL_MODEL } from "@models/materials/IMaterialModel";
import { MaterialModel } from "@models/materials/MaterialModel";
import { IMPORTER_MODEL } from "@models/importer/IImporterModel";
import { WebImporterModel } from "@models/importer/WebImporterModel";
import {
  PixyzConversionModel,
  PIXYZ_CONVERSION_MODEL,
} from "@models/importer/PixyzConversionModel";
import { PendingImportsModel } from "@models/importer/PendingImportsModel";
import { PENDING_IMPORTS_MODEL } from "@models/importer/IPendingImportsModel";
import {
  AIOrchestratorModel,
  AI_ORCHESTRATOR_MODEL,
} from "@ai/orchestrator/AIOrchestratorModel";
import { SPARK_BEHAVIOR_HOST } from "@runtime/assets/sparkBehavior";
import { AI_INTERFACE_MODEL } from "@ai/interface/AIInterfaceModel";
import { AI_AGENT_TOOLS_MODEL } from "@ai/registry/AIAgentToolRegistryModel";
import {
  REALTIME_CONVERSATION_LOG,
  type IRealtimeConversationLog,
} from "@ai/observability/RealtimeConversationLog";
import { AI_CONTEXT_ENGINE_MODEL } from "@ai/context/AIContextEngineModel.Core";
import { isAiEnabled } from "@ai/aiConfig";
import { InMemoryToastsModel } from "@models/notifications/InMemoryToastsModel";
import type { IToastsModel } from "@models/notifications/IToastsModel";
import { TOASTS_MODEL } from "@models/notifications/IToastsModel";
import type { ISharedUsersModel } from "@models/sharing/ISharedUsersModel";
import { SHARED_USERS_MODEL } from "@models/sharing/ISharedUsersModel";
import { SharedUsersModel } from "@models/sharing/SharedUsersModel";
import type { IDeepLinksHandlerModel } from "@models/sharing/IDeepLinksHandlerModel";
import { DEEP_LINKS_HANDLER_MODEL } from "@models/sharing/IDeepLinksHandlerModel";
import { DeepLinksHandlerModel } from "@models/sharing/DeepLinksHandlerModel";
import { ConfirmationModel } from "@models/notifications/ConfirmationModel";
import { CONFIRMATION_MODEL, type ConfirmationArgs } from "@models/notifications/IConfirmationModel";
import { APP_CACHE_MODEL } from "@models/cache/IAppCacheModel";
import { AppCacheModel } from "@models/cache/AppCacheModel";
import type { IAuthModel } from "@models/auth/IAuthModel";
import { AUTH_MODEL } from "@models/auth/IAuthModel";
import { AmplifyAuthModel } from "@models/auth/AmplifyAuthModel";
import { DevAutoLoginAuthModel } from "@models/auth/DevAutoLoginAuthModel";
import { loadAuthConfig } from "@models/auth/authConfig";
import type { IStorageModel } from "@models/storage/IStorageModel";
import { STORAGE_MODEL } from "@models/storage/IStorageModel";
import { HttpStorageModel } from "@models/storage/HttpStorageModel";
import { resetAwsCredentialClients } from "@models/storage/awsCredentialClientRegistry";
import { clearProfilePicCache } from "@models/storage/profilePicCache";
import { clearWorkflowPresenceCache } from "@models/workflow/workflowPresenceCache";
import {
  ADMIN_STORAGE_MODEL,
  HttpAdminStorageModel,
} from "@models/storage/HttpAdminStorageModel";
import type { ICustomerModel } from "@models/customer/ICustomerModel";
import { CUSTOMER_MODEL } from "@models/customer/ICustomerModel";
import { HttpCustomerModel } from "@models/customer/HttpCustomerModel";
import type { IEventModel } from "@models/events/IEventModel";
import { EVENT_MODEL } from "@models/events/IEventModel";
import { WebSocketEventModel } from "@models/events/WebSocketEventModel";
import {
  TELEMETRY_SERVICE,
  type ITelemetryService,
} from "@models/telemetry/ITelemetryService";
import { AwsTelemetryModel } from "@models/telemetry/AwsTelemetryModel";
import { wireProjectEventReconciliation } from "@models/events/projectEventReconciliation";
import type { IMultiuserSessionModel } from "@models/multiuser/IMultiuserSessionModel";
import { MULTIUSER_SESSION_MODEL } from "@models/multiuser/IMultiuserSessionModel";
import type { IPermissionsModel } from "@models/permissions/IPermissionsModel";
import { PERMISSIONS_MODEL } from "@models/permissions/IPermissionsModel";
import { PermissionsModel } from "@models/permissions/PermissionsModel";
import { bindPresenterDelegationEffects } from "@models/permissions/presenterDelegationEffects";
import { MultiuserSessionModel } from "@models/multiuser/MultiuserSessionModel";
import type { IAnnotationsModel } from "@models/annotations/IAnnotationsModel";
import { ANNOTATIONS_MODEL } from "@models/annotations/IAnnotationsModel";
import { CloudAnnotationsModel } from "@models/annotations/CloudAnnotationsModel";
import type { ISketchModel } from "@models/annotations/ISketchModel";
import { SKETCH_MODEL } from "@models/annotations/ISketchModel";
import { SketchModel } from "@models/annotations/SketchModel";
import {
  PROJECT_WORKFLOW_MODEL,
  type IProjectWorkflowModel,
} from "@models/workflow/IProjectWorkflowModel";
import { ProjectWorkflowModel } from "@models/workflow/ProjectWorkflowModel";
import {
  WORKFLOW_MEDIA_PREFETCHER,
  WorkflowMediaPrefetcher,
} from "@models/workflow/WorkflowMediaPrefetcher";
import {
  PROJECT_DOCS_MODEL,
  type IProjectDocsModel,
} from "@models/docs/IProjectDocsModel";
import { ProjectDocsModel } from "@models/docs/ProjectDocsModel";
import {
  PRODUCT_DOCS_STORE_MODEL,
  type IProductDocsStoreModel,
} from "@models/docs/IProductDocsStoreModel";
import { ProductDocsStoreModel } from "@models/docs/ProductDocsStoreModel";
import { BrowserDocumentOpenService } from "@runtime/browser/BrowserDocumentOpenService";
import { UI_SOUND_SERVICE, UiSoundService, type IUiSoundService } from "@runtime/audio/UiSoundService";
import { connectAISoundCues } from "@ai/interface/aiSoundCues";
import {
  MEDIA_LIBRARY_MODEL,
  type IMediaLibraryModel,
} from "@models/medialibrary/IMediaLibraryModel";
import { MediaLibraryModel } from "@models/medialibrary/MediaLibraryModel";
import {
  CUSTOMER_VECTOR_STORE_MODEL,
  type ICustomerVectorStoreModel,
} from "@ai/memory/ICustomerVectorStoreModel";
import { CustomerVectorStoreModel } from "@ai/memory/CustomerVectorStoreModel";
import {
  WORKFLOW_RUN_MODEL,
  type IWorkflowRunModel,
} from "@models/workflow/IWorkflowRunModel";
import {
  WORKFLOW_ACTIVITY_SERVICE,
  type IWorkflowActivityService,
} from "@models/workflow/IWorkflowActivityService";
import { AwsWorkflowActivityModel } from "@models/workflow/AwsWorkflowActivityModel";
import {
  WORKFLOW_USER_STATE_SERVICE,
  type IWorkflowUserStateService,
} from "@models/workflow/IWorkflowUserStateService";
import { AwsWorkflowUserStateModel } from "@models/workflow/AwsWorkflowUserStateModel";
import {
  AI_TRANSCRIPT_SERVICE,
  type IAITranscriptService,
} from "@ai/transcript/IAITranscriptService";
import { AwsAITranscriptModel } from "@ai/transcript/AwsAITranscriptModel";

/**
 * Central IOC registration point.
 *
 * Mirrors the Unity bootstrap pattern where IOC registrations live in a known
 * startup path (see `shared-application-code/Shared/Assets/Campfire/Runtime/Infrastructure`
 * and `AppConfig.cs`). Register interface-tokens → concrete classes here so
 * consumers can depend on `Ioc.resolve(TOKEN)` only.
 *
 * Keep this file flat and explicit. Do not auto-register via side-effects from
 * model files; that hurts tree-shaking and makes the bootstrap order opaque.
 */
export function registerCoreServices(): void {
  // Runtime infrastructure (M0–M1).
  const frame = new FrameModel();
  Ioc.registerInstance<FrameModel>(FRAME_MODEL, frame);
  // Viewport render settings. Registered in the core pass rather than at
  // viewport mount so the stats panel can bind them before a composer exists;
  // `SelectionOutlineController` picks the live values up when it builds.
  // Render quality goes in BEFORE `EngineService`, which reads it for the
  // pixel ratio on its very first resize.
  Ioc.registerInstance<RenderQualityModel>(RENDER_QUALITY_MODEL, new RenderQualityModel());
  Ioc.registerInstance<AmbientOcclusionModel>(
    AMBIENT_OCCLUSION_MODEL,
    new AmbientOcclusionModel(),
  );
  Ioc.registerInstance<EngineService>(ENGINE_SERVICE, new EngineService());
  Ioc.registerInstance<InputService>(INPUT_SERVICE, new InputService());
  Ioc.registerInstance<PickingService>(PICKING_SERVICE, new PickingService());
  // The scene renderer attaches to this when it mounts; registered here so a
  // workflow replay or Spark's "play that again" resolves it whether or not a
  // viewport has mounted yet (AI-1459). Inert with nothing attached.
  Ioc.registerInstance<EntityAnimationPlayback>(
    ENTITY_ANIMATION_PLAYBACK,
    new EntityAnimationPlayback(),
  );
  // Scene statistics read-out (Unity `SceneStatisticsViewModel`). Registered
  // here rather than at viewport mount so the toggle can resolve it before a
  // renderer exists; `ViewportView` hands it the live engine via
  // `attachEngine`. Inert until the panel opens.
  Ioc.registerInstance<SceneStatisticsService>(
    SCENE_STATISTICS_SERVICE,
    new SceneStatisticsService(frame),
  );
  // WebXR session lifecycle (SceneStatisticsService pattern: headless in the
  // core pass, `ViewportView` attaches the live engine + camera on mount).
  // The panel registry is the Vue ⇄ renderer seam for the XR slates.
  Ioc.registerInstance<XRSessionService>(XR_SESSION_SERVICE, new XRSessionService());
  Ioc.registerInstance<XRPanelSourceRegistry>(
    XR_PANEL_SOURCE_REGISTRY,
    new XRPanelSourceRegistry(),
  );
  // Console placement (Quest `IConsolePlacementModel`): where the Campfire
  // STAGE sits in the wearer's real room. Headless and dependency-free, but it
  // must be in the CORE pass — `XRUniversalMenuView` binds it through
  // `useConsolePlacement`, and `ViewportView` hands the pose to the renderer
  // half, so nothing view-scoped can own it.
  const consolePlacement = new ConsolePlacementModel();
  Ioc.registerInstance<IConsolePlacementModel>(
    CONSOLE_PLACEMENT_MODEL,
    consolePlacement,
  );
  // Editor left-region panel visibility (Unity `IEditorUIModel`, implemented by
  // `EditorViewModel`). Dependency-free, so it has no ordering constraint — but
  // it must be registered in the CORE pass, not at view mount: Spark's play mode
  // drives it, and `ProjectView` destroys the left region with `v-if` while a
  // workflow runs, so nothing view-scoped can own it.
  Ioc.registerInstance<IEditorUIModel>(EDITOR_UI_MODEL, new EditorUIModel());
  // App-wide developer-affordance gate (web-only; typing `potato` into the
  // Spark prompt flips it). Same reason it belongs in the core pass: it is
  // written from the Spark panel and read by the scenes bar, the statistics
  // composable, and the workflow ViewModel, none of which can own it.
  const debugMode = new DebugModeModel();
  Ioc.registerInstance<IDebugModeModel>(DEBUG_MODE_MODEL, debugMode);
  // Synthesized interface sounds (web-only). Dependency-free, and its audio
  // context starts on the first cue, so registering it here costs nothing.
  const uiSound = new UiSoundService();
  Ioc.registerInstance<IUiSoundService>(UI_SOUND_SERVICE, uiSound);
  // What else is published on this origin (`/deployments.json` — the default
  // build plus any named channels). Read ONLY by the AI debug panel's Settings
  // tab; nothing in the app branches on it. Registered here rather than in a
  // later pass because it is a static same-origin file with no auth or storage
  // dependency, so unlike `AppSettingsModel` it needs no `attachTransport`.
  Ioc.registerInstance<IDeploymentsModel>(DEPLOYMENTS_MODEL, new DeploymentsModel());
  // The account's SHARED settings document (`settings/app-settings.json`) —
  // the AI skill gate plus the dev-panel overrides below, which used to be
  // per-browser `localStorage` preferences and are now account-wide.
  //
  // Registered HERE, in the core pass, because `DevSettingsModel` projects it
  // and is itself read by a headless model; its cloud transport is attached
  // further down, once auth + storage exist (`attachTransport`). Until then it
  // serves stock defaults, which is the same state it is in while the first
  // load is in flight — so no reader needs an extra case for it.
  const appSettings = new AppSettingsModel();
  Ioc.registerInstance<IAppSettingsModel>(APP_SETTINGS_MODEL, appSettings);
  // Per-feature developer OVERRIDES behind that gate (the debug dialog's "Dev
  // settings" tab). Takes the gate as a dependency because every effective
  // value folds it in, and the settings document as the one that actually
  // stores them. Registered right after the gate for the same core-pass
  // reason: one of its readers is the headless workflow review model.
  const devSettings = new DevSettingsModel(debugMode, appSettings);
  Ioc.registerInstance<IDevSettingsModel>(DEV_SETTINGS_MODEL, devSettings);
  // The deployment's PUBLISHED agent prompts — the role / skill / personality /
  // base Markdown the model is given, where an edited one has been published,
  // plus the workflow schema. Registered here rather than in the AI pass because
  // it is read by the headless catalog loaders, which run before any view mounts.
  // Its cloud transport is attached further down, once auth + storage exist; the
  // debug gate it takes now guards WRITES only (see the interface header).
  const promptStore = new AIPromptStoreModel(debugMode);
  Ioc.registerInstance<IAIPromptStoreModel>(AI_PROMPT_STORE_MODEL, promptStore);

  // "What may this user do, here, right now" — the port of Unity's
  // `PermissionsModel`. Registered in the CORE pass and BEFORE `ProjectModel`,
  // whose `activeUserRole` getter delegates to it (lazily, so the order here
  // is belt not brace). Its collaborators — auth, multiuser, the admin GraphQL
  // API — are registered much further down, so it gets them via
  // `attachTransport` once they exist; until then every predicate answers from
  // the least-privilege default, which is also its state while the first role
  // fetch is in flight. Do NOT reorder the passes to avoid the two-phase
  // setup (`Campfire_Web_Runtime.md:34`).
  const permissions = new PermissionsModel();
  Ioc.registerInstance<IPermissionsModel>(PERMISSIONS_MODEL, permissions);

  // Project / scene / selection / undo (M4 — in-memory implementations).
  // Cloud-backed swap arrives in M5–M6; the IOC tokens stay the same.
  const project = new ProjectModel();
  const selection = new SelectionModel(project);
  const scene = new SceneModel(project);
  const skybox = new SkyboxModel(project, scene);
  Ioc.registerInstance<ProjectModel>(PROJECT_MODEL, project);
  Ioc.registerInstance<SceneModel>(SCENE_MODEL, scene);
  Ioc.registerInstance<SkyboxModel>(SKYBOX_MODEL, skybox);
  Ioc.registerInstance<SkyboxImportModel>(
    SKYBOX_IMPORT_MODEL,
    new SkyboxImportModel(project, skybox),
  );
  Ioc.registerInstance<SelectionModel>(SELECTION_MODEL, selection);
  Ioc.registerInstance<HoverModel>(HOVER_MODEL, new HoverModel());
  // Hierarchy edit models: Unity's `DuplicationModel` (copy/paste/duplicate +
  // copy/paste position) and `MergeModel` (merge / un-merge parts).
  Ioc.registerInstance<IDuplicationModel>(DUPLICATION_MODEL, new DuplicationModel(project));
  Ioc.registerInstance<IMergeModel>(MERGE_MODEL, new MergeModel(project));
  const undoRedo = new UndoRedo();
  Ioc.registerInstance<UndoRedo>(UNDO_REDO, undoRedo);

  // Project catalog — always the real DynamoDB-backed catalog (M6).
  // The in-memory fixture catalog was removed with the stub-auth path;
  // dev runs auto-login into the dev account instead (see below).
  const projectList: IProjectListModel = new HttpProjectListModel();
  // Admin GraphQL API (cross-account share pipeline + role ceilings).
  Ioc.registerInstance<HttpAdminStorageModel>(
    ADMIN_STORAGE_MODEL,
    new HttpAdminStorageModel(),
  );
  Ioc.registerInstance<IProjectListModel>(PROJECT_LIST_MODEL, projectList);
  // Asset library catalog (Unity hosts this on CustomerModel — see the
  // ownership note in IAssetLibraryModel).
  const assetLibrary = new AssetLibraryModel();
  Ioc.registerInstance<IAssetLibraryModel>(ASSET_LIBRARY_MODEL, assetLibrary);

  // Toolbar / toasts — placeholder implementations until M6 / M9 promote
  // them to feature-complete versions. The interfaces stay stable.
  // The toolbar model takes the permissions model so it can refuse a denied
  // tool and disarm one that stops being allowed, whichever panels are mounted.
  const toolbar = new InMemoryToolbarModel(undoRedo, permissions);
  Ioc.registerInstance<InMemoryToolbarModel>(TOOLBAR_MODEL, toolbar);
  // Handing the presenter role to Spark (a workflow run, or an author's
  // preview) clears the selection and pauses undo until it is handed back.
  bindPresenterDelegationEffects({ permissions, selection, undoRedo });
  const placement = new PlacementModel(project);
  Ioc.registerInstance<PlacementModel>(PLACEMENT_MODEL, placement);
  Ioc.registerInstance<ISurfacePlacementModel>(
    SURFACE_PLACEMENT_MODEL,
    new SurfacePlacementModel(project, selection),
  );
  // M9 tools: the per-scene cutting plane + the Measure bounding box.
  Ioc.registerInstance<ICommonSliceToolModel>(
    COMMON_SLICE_TOOL_MODEL,
    new CommonSliceToolModel(project),
  );
  Ioc.registerInstance<IBoundingBoxToolModel>(
    BOUNDING_BOX_TOOL_MODEL,
    new BoundingBoxToolModel(project),
  );
  // The Measure tool's other half: Point-to-Point rulers. Also owns the shared
  // mode switch and the display unit both modes read.
  Ioc.registerInstance<IMeasurementsModel>(MEASUREMENTS_MODEL, new MeasurementsModel(project));
  const explosion = new ExplosionModifierModel(project);
  Ioc.registerInstance<IExplosionModifierModel>(EXPLOSION_MODIFIER_MODEL, explosion);
  Ioc.registerInstance<LaserToolModel>(
    LASER_TOOL_MODEL,
    new LaserToolModel(),
  );
  // Grab tool (M9): headless option/commit model — the runtime
  // GrabToolController + NetworkGrabUpdateModel plug into it. Spin playback
  // is data-driven off snapshot rows (runtime/assets/persistentSpinRuntime),
  // so the old SpinModifierModel stub is gone.
  Ioc.registerInstance<IGrabModifierModel>(
    GRAB_MODIFIER_MODEL,
    new GrabModifierModel(project, undoRedo),
  );
  const materials = new MaterialModel(project);
  Ioc.registerInstance<IMaterialModel>(MATERIAL_MODEL, materials);
  // Model import ("Add Model") — in-browser glTF parse → part/snapshot synthesis.
  const importer = new WebImporterModel();
  Ioc.registerInstance(IMPORTER_MODEL, importer);
  // Cloud CAD conversion (Pixyz) — STEP/SLDPRT/… → Step Functions → GLB.
  const pixyzConversion = new PixyzConversionModel();
  Ioc.registerInstance(PIXYZ_CONVERSION_MODEL, pixyzConversion);
  // In-flight imports → hierarchy loading rows.
  Ioc.registerInstance(PENDING_IMPORTS_MODEL, new PendingImportsModel());
  Ioc.registerInstance<InMemoryToastsModel>(TOASTS_MODEL, new InMemoryToastsModel());
  // App settings' cache readout + "Clear Cache" (Unity `IAppCache`).
  Ioc.registerInstance<AppCacheModel>(APP_CACHE_MODEL, new AppCacheModel());
  // Confirmation dialogs: the web stand-in for Unity navigating to
  // `ViewNames.Confirmation`. Rendered by `ConfirmationHost` in `App.vue`.
  const confirmations = new ConfirmationModel();
  Ioc.registerInstance<ConfirmationModel>(CONFIRMATION_MODEL, confirmations);
  if (import.meta.env.DEV) {
    // Dev QA hook (same pattern as `window.__campfireInjectFakeUsers`): raise
    // any confirmation shape without having to reach its real trigger.
    // e.g. await window.__campfireConfirm({headerText:"…", confirmText:"OK", cancelText:""})
    (window as unknown as Record<string, unknown>).__campfireConfirm = (
      args: ConfirmationArgs,
    ) => confirmations.request(args);
  }

  // Developers choose explicit or automatic login in their ignored
  // `.env.local`; explicit login remains the safe default.
  const authConfig = loadAuthConfig();
  const amplifyAuth = new AmplifyAuthModel();
  const auth: IAuthModel = authConfig.mode === "auto"
    ? new DevAutoLoginAuthModel(amplifyAuth, authConfig.autoLoginCredentials)
    : amplifyAuth;
  Ioc.registerInstance<IAuthModel>(AUTH_MODEL, auth);
  const authWakeRefresh = new AuthWakeRefreshService(() =>
    auth.ensureFreshTokens(),
  );
  Ioc.registerInstance<IAuthWakeRefreshService>(
    AUTH_WAKE_REFRESH_SERVICE,
    authWakeRefresh,
  );
  authWakeRefresh.start();

  // Cloud transport — always the real Dynamo + S3 storage (browser AWS
  // SDK, mirroring Unity's `DynamoSdkclient` path) and the real sessions
  // websocket. The offline stubs are no longer registered anywhere.
  // The signed-in name is read at write time to stamp the project row's
  // last editor (`ModifiedBy`, AI-1501) — the "by" on each project card.
  const storage = new HttpStorageModel(undefined, () => auth.currentUser?.displayName);
  const customer = new HttpCustomerModel();
  Ioc.registerInstance<IStorageModel>(STORAGE_MODEL, storage);
  Ioc.registerInstance<ICustomerModel>(CUSTOMER_MODEL, customer);
  const telemetry = new AwsTelemetryModel();
  const workflowActivity = new AwsWorkflowActivityModel();
  const workflowUserState = new AwsWorkflowUserStateModel();
  const transcript = new AwsAITranscriptModel();
  Ioc.registerInstance<ITelemetryService>(TELEMETRY_SERVICE, telemetry);
  Ioc.registerInstance<IWorkflowActivityService>(
    WORKFLOW_ACTIVITY_SERVICE,
    workflowActivity,
  );
  Ioc.registerInstance<IWorkflowUserStateService>(
    WORKFLOW_USER_STATE_SERVICE,
    workflowUserState,
  );
  Ioc.registerInstance<IAITranscriptService>(AI_TRANSCRIPT_SERVICE, transcript);

  // The DEPLOYMENT's agent configuration — published prompts and AI settings.
  // Deliberately NOT `IStorageModel`: that interface addresses customer cloud
  // data (`account/{customerId}/projectdata/…`), and neither document is
  // customer data. They live on the host that serves the app, keyed by
  // deployment, behind `/__campfire_config/` (`deploy/config-service/`).
  //
  // `auth` is still passed, but only to stamp "who changed this" and to obtain
  // the token a WRITE needs — a read is unauthenticated, which is why both
  // models can now load before anyone signs in.
  const deploymentConfig = new DeploymentConfigModel(auth);
  Ioc.registerInstance<IDeploymentConfigModel>(DEPLOYMENT_CONFIG_MODEL, deploymentConfig);
  appSettings.attachTransport(auth, deploymentConfig);
  // Same two-phase arrangement, same reasons: the catalogs read it synchronously
  // during a boot that happens before auth exists, so it serves "nothing
  // published" until this lands and tells its readers through `onChanged`.
  promptStore.attachTransport(auth, deploymentConfig);

  // Comments/annotations — cloud-backed (M8). Registered after project + auth
  // + storage exist (it resolves the active project id + customer id + storage
  // transport). Loads once per project on active-project change; optimistic
  // writes to the same Dynamo rows Unity authors, with rollback + error toast
  // on failure. Replaces the M8-first-slice `InMemoryAnnotationsModel`.
  const annotations = new CloudAnnotationsModel(project, auth, storage);
  Ioc.registerInstance<IAnnotationsModel>(ANNOTATIONS_MODEL, annotations);
  // The editor's right rail (Unity `IRightPanelUIModel`) — the half of Show UI /
  // Hide UI that `EditorUIModel` above does not own. After the annotations
  // model because the rail's comments PAGE lives there.
  Ioc.registerInstance<IRightPanelUIModel>(RIGHT_PANEL_UI_MODEL, new RightPanelUIModel(annotations));
  // Sketch image pipeline (M9 Sketch tool) — owns the three `Sketchs/{id}*.png`
  // blob caches; the annotation ROW lives in the annotations model above.
  const sketches = new SketchModel(project, auth, storage);
  Ioc.registerInstance<ISketchModel>(SKETCH_MODEL, sketches);
  // Local sketch saves broadcast `sketch_uploaded` (tc 36) so peers re-fetch
  // the PNGs (Unity `MultiuserSessionModel.OnSketchUploaded`).
  sketches.onSketchUploaded.on(({ sketchId }) => {
    project.publishLiveProjectEvent("sketch_uploaded", { sketchId, SketchId: sketchId });
  });

  // Workflow-product document source. Documents are queried from DynamoDB by
  // `workflow.product`; browser-side management remains read-only until the
  // Define app exposes a client-safe write contract.
  const productDocs = new ProductDocsStoreModel(storage, new BrowserDocumentOpenService());
  Ioc.registerInstance<IProductDocsStoreModel>(PRODUCT_DOCS_STORE_MODEL, productDocs);

  // Per-project workflow.yaml model (Ai-skill-load port). Registered
  // unconditionally like the annotations model above — Unity's StorageModel
  // owns it regardless of the AI gate, and it is inert with no active project;
  // its AI consumers (workflow tools / run model) are the ones gated by
  // isAiEnabled().
  const workflow = new ProjectWorkflowModel(project, auth, storage, workflowUserState, productDocs);
  Ioc.registerInstance<IProjectWorkflowModel>(PROJECT_WORKFLOW_MODEL, workflow);

  // AI memory (harness layer 5) — the customer's OpenAI vector store, shared
  // with the Unity client. Gated by the AI flag: the docs library still works
  // with Spark off, documents are simply not searchable.
  if (isAiEnabled()) {
    Ioc.registerInstance<ICustomerVectorStoreModel>(
      CUSTOMER_VECTOR_STORE_MODEL,
      new CustomerVectorStoreModel(storage),
    );
  }
  // The document library surface, scoped by the loaded workflow product.
  const docs = new ProjectDocsModel(project, auth, workflow, productDocs);
  Ioc.registerInstance<IProjectDocsModel>(PROJECT_DOCS_MODEL, docs);

  // The account's media library — the images / videos a workflow step points
  // at. Account-wide with no project join (see `IMediaLibraryModel`), and not
  // AI-gated: media is authored content, and the play panel that shows it is
  // reachable with Spark off.
  const mediaLibrary = new MediaLibraryModel(project, auth, storage);
  Ioc.registerInstance<IMediaLibraryModel>(MEDIA_LIBRARY_MODEL, mediaLibrary);

  // Warms the byte cache for a workflow's media once the project has finished
  // loading, so a guided run plays its videos from disk. Not AI-gated, for the
  // same reason the library above is not. See the class header for why it
  // waits for the load rather than starting with the document.
  Ioc.registerInstance<WorkflowMediaPrefetcher>(
    WORKFLOW_MEDIA_PREFETCHER,
    new WorkflowMediaPrefetcher(project, workflow, mediaLibrary),
  );

  const events: IEventModel = new WebSocketEventModel(telemetry, auth);

  Ioc.registerInstance<IEventModel>(EVENT_MODEL, events);
  wireProjectEventReconciliation(project, events);
  // Live grab streaming wire adapter (grabtool_operation, tc 18).
  Ioc.registerInstance<NetworkGrabUpdateModel>(
    NETWORK_GRAB_UPDATE_MODEL,
    new NetworkGrabUpdateModel(project, events),
  );
  // Inbound `sketch_uploaded`: a peer finished uploading a sketch's PNGs —
  // purge cached layers + mark the row valid so the pin refreshes (Unity
  // `ProjectLiveUpdatesModel.OnServerUploadSketch` → `DownloadSketchTexture`).
  // Own echoes are filtered by app-instance id like Unity's listener.
  events.onEvent.on((event) => {
    if (event.type.toLowerCase() !== "sketch_uploaded") return;
    const payload = event.payload as Record<string, unknown> | null;
    if (!payload) return;
    const aid = typeof payload.aid === "string" ? payload.aid : null;
    if (aid && aid === window.sessionStorage.getItem("campfire.web.appInstanceId")) return;
    const sketchId =
      typeof payload.sketchId === "string" && payload.sketchId
        ? payload.sketchId
        : typeof payload.SketchId === "string"
          ? payload.SketchId
          : null;
    if (!sketchId) return;
    sketches.applyRemoteSketchUploaded(sketchId);
    annotations.applySketchUploadedFromCloud(sketchId);
  });
  const multiuser = new MultiuserSessionModel(auth, events, project);
  Ioc.registerInstance<IMultiuserSessionModel>(MULTIUSER_SESSION_MODEL, multiuser);
  // The permissions model can finally see who the user is, which project is
  // open and whether they are presenting. Everything it gates was answering
  // "least privilege" until this line.
  permissions.attachTransport({
    auth,
    multiuser,
    project,
    adminStorage: Ioc.resolve<HttpAdminStorageModel>(ADMIN_STORAGE_MODEL),
    customer: Ioc.resolve<ICustomerModel>(CUSTOMER_MODEL),
    storage,
  });
  // Project sharing (Unity `SharedUsersModel` + the invite / revoke legs of
  // `ShareViewModel`): the roster, the invite rules, the link rows, the
  // Visited flip on project open, and the outbound `change_sharing` /
  // `evict_user` events. Needs everything above — auth, storage, the admin
  // GraphQL API, the customer's plan, the resolved project role, the live
  // session roster and the session socket — which is why it is registered
  // this late.
  Ioc.registerInstance<ISharedUsersModel>(
    SHARED_USERS_MODEL,
    new SharedUsersModel({
      auth,
      project,
      storage,
      admin: Ioc.resolve<HttpAdminStorageModel>(ADMIN_STORAGE_MODEL),
      customer: Ioc.resolve<ICustomerModel>(CUSTOMER_MODEL),
      permissions,
      multiuser,
      toasts: Ioc.resolve<IToastsModel>(TOASTS_MODEL),
      events,
      // The model owns the email's absolute URL but stays headless; the
      // composition root supplies the current origin + Vite channel base.
      shareBaseUrl: new URL(import.meta.env.BASE_URL, window.location.origin).toString(),
    }),
  );
  Ioc.registerInstance<IDeepLinksHandlerModel>(
    DEEP_LINKS_HANDLER_MODEL,
    new DeepLinksHandlerModel(auth, storage, projectList),
  );
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "hidden") return;
    void telemetry.flushPending();
    void workflowActivity.flushPending();
    void workflowUserState.flushPending().catch((error) => {
      console.warn("[IOC] Failed to flush workflow user state:", error);
    });
    void transcript.flushPending();
  });
  window.addEventListener("pagehide", (event) => {
    if (event.persisted) return;
    multiuser.stop();
    // Close the session socket CLEANLY (code 1000) after `stop()` has queued
    // `close_project` + `quit_application` — `close()` flushes the send buffer
    // before the close frame. The sessions service drops a connection's
    // participant row on a clean close but keeps an abruptly-dropped one
    // until its heartbeat times out: a reload (same app-instance id, from
    // `sessionStorage`) therefore left a GHOST row per reload — eight were
    // observed in one snapshot on 2026-10-09 — and when the service finally
    // reaped a ghost it broadcast `quit_application` for an id that was
    // ALIVE AGAIN, so every peer marked the reloaded user offline (and, if
    // they were presenting, demoted them and self-elected) until their next
    // heartbeat revived them. `disconnect()` also stops the reconnect timer,
    // which has no business firing on a page that is going away.
    events.disconnect();
    void telemetry.flushPending();
    void workflowActivity.flushPending();
    void workflowUserState.flushPending().catch((error) => {
      console.warn("[IOC] Failed to flush workflow user state:", error);
    });
    void transcript.flushPending();
  });

  // Active-tool presence (`active_tool`, tc 0). Mirrors Unity's
  // `DesktopLocalUserModel.OnActivePresenterToolChanged` subscribing to
  // `BaseToolsModel.ActiveToolChanged` — the composition root owns the
  // cross-model wiring so neither model imports the other.
  toolbar.onActiveToolChanged.on((tool) => {
    multiuser.setLocalActiveTool(presenterToolFor(tool));
  });


  // Spark AI harness (M11). The orchestrator is the composition root for
  // the 7 layers (registry/executor/context/guardrails/observability/
  // interface/session); see `src/ai/RULE.mdc`. Gated by `ai_enabled`
  // (`VITE_AI_ENABLED`) like Unity's `AppConfig.AIEnabled`.
  const orchestrator = new AIOrchestratorModel({
    project,
    selection,
    placement,
    explosion,
    materials,
    projectList,
    undoRedo,
    auth,
    telemetry,
    workflow,
    workflowActivity,
    transcript,
    prompts: promptStore,
    devSettings,
    appSettings,
    consolePlacement,
    docs,
    vectorStore: Ioc.isRegistered<ICustomerVectorStoreModel>(CUSTOMER_VECTOR_STORE_MODEL)
      ? Ioc.resolve<ICustomerVectorStoreModel>(CUSTOMER_VECTOR_STORE_MODEL)
      : null,
  });
  Ioc.registerInstance<AIOrchestratorModel>(AI_ORCHESTRATOR_MODEL, orchestrator);
  Ioc.registerInstance(AI_INTERFACE_MODEL, orchestrator.interfaceModel);
  // Spark's sounds hang off the interface model here, once, rather than off a
  // panel: see `aiSoundCues.ts`.
  connectAISoundCues(orchestrator.interfaceModel, uiSound);
  Ioc.registerInstance(AI_AGENT_TOOLS_MODEL, orchestrator.toolsModel);
  Ioc.registerInstance(AI_CONTEXT_ENGINE_MODEL, orchestrator.contextEngineModel);
  // Developer wire log, read by the debug panel's Log tab.
  Ioc.registerInstance<IRealtimeConversationLog>(
    REALTIME_CONVERSATION_LOG,
    orchestrator.conversationLog,
  );
  Ioc.registerInstance<IWorkflowRunModel>(WORKFLOW_RUN_MODEL, orchestrator.workflowRunModel);
  // Spark's 3D head registers against this seam when the viewport mounts
  // (Unity: `SparkBehavior` ⇄ `AIOrchestratorModel.RegisterSparkBehavior`).
  Ioc.registerInstance(SPARK_BEHAVIOR_HOST, orchestrator);
  if (import.meta.env.DEV) {
    // Dev QA hooks (same pattern as `window.__campfireInjectFakeUsers`).
    const dev = window as unknown as Record<string, unknown>;
    dev.__campfireSpark = orchestrator;
    // Import a served model by URL, bypassing the OS file picker. Routes GLB →
    // direct parse, CAD (STEP/SLDPRT/…) → cloud conversion.
    // e.g. await window.__campfireImportModel('/CASE_iPHONE6.SLDPRT')
    dev.__campfireImportModel = async (url: string) => {
      const res = await fetch(url);
      const blob = await res.blob();
      const name = url.split("/").pop() || "model.glb";
      const file = new File([blob], name, { type: "application/octet-stream" });
      if (importer.isSupportedFile(name)) {
        const parsed = await importer.importFromFileAsync(file);
        return project.importModelAsync(parsed);
      }
      if (pixyzConversion.isCadFile(name)) {
        return pixyzConversion.convertAndImportAsync(file);
      }
      throw new Error(`Unsupported file: ${name}`);
    };
    // Direct handles on the project + storage models, so a cloud round-trip
    // (row written? correct attributes? object in S3?) can be asserted from the
    // console without reaching for the AWS CLI. Same DEV-only gate and intent
    // as `__campfireConfirm` — reach state that a UI-driven path can't easily
    // show. Never referenced by app code.
    dev.__campfireProject = project;
    dev.__campfireStorage = Ioc.resolve<IStorageModel>(STORAGE_MODEL);
    // The per-project workflow file. Exposed so a verification can drive the
    // YAML API path (`applyYamlAsync` / `getWorkflowYamlText`) directly — the
    // panel only reaches it through the editor and the builder agent.
    dev.__campfireWorkflow = workflow;
    dev.__campfireWorkflowUserState = workflowUserState;
    // The account's document library. Exposed so a verification can upload /
    // list / delete a doc and read the resulting manifest without walking the
    // library dialog and the OS file picker.
    dev.__campfireDocs = docs;
    dev.__campfireMeasurements = Ioc.resolve<IMeasurementsModel>(MEASUREMENTS_MODEL);
    dev.__campfirePicking = Ioc.resolve<PickingService>(PICKING_SERVICE);
  }

  const startAi = () => {
    if (!isAiEnabled()) return;
    void orchestrator.startAsync();
  };

  auth.onSignedIn.on(() => {
    void events.connectAsync();
    multiuser.start();
    startAi();
  });
  auth.onInitialized.on(() => {
    if (!auth.currentUser) return;
    void events.connectAsync();
    multiuser.start();
    startAi();
  });
  const stopAuthenticatedServices = () => {
    orchestrator.stop();
    multiuser.stop();
    events.disconnect();
  };
  const clearAuthenticatedState = () => {
    stopAuthenticatedServices();

    // AWS SDK clients memoize the temporary identity-pool credentials resolved
    // by their provider. Amplify clears its own credential cache on sign-out,
    // but that does not invalidate clients which already resolved the old
    // keys. Rebuild those clients before another user signs in, and clear every
    // account-scoped model cache alongside them. A full page reload used to do
    // this accidentally, which is why switching users only worked after one.
    storage.resetCloudEnvironment();
    resetAwsCredentialClients();
    customer.reset();
    assetLibrary.reset();
    mediaLibrary.reset();
    docs.reset();
    if (Ioc.isRegistered<ICustomerVectorStoreModel>(CUSTOMER_VECTOR_STORE_MODEL)) {
      Ioc.resolve<ICustomerVectorStoreModel>(CUSTOMER_VECTOR_STORE_MODEL).reset();
    }
    clearProfilePicCache();
    clearWorkflowPresenceCache();
  };
  auth.onSignedOut.on(clearAuthenticatedState);
  auth.onSessionExpired.on(clearAuthenticatedState);
  if (auth.currentUser) {
    void events.connectAsync();
    multiuser.start();
    startAi();
  }
}

